import type { HelpArticle } from "./useHelpArticles";

export interface HelpSearchResult {
  article: HelpArticle;
  /** The best-matching heading, when the match is inside a section. */
  section?: { id: string; title: string };
  snippet: string;
  score: number;
}

const WEIGHT = { title: 12, keyword: 8, summary: 5, heading: 4, text: 1 } as const;
const SNIPPET_RADIUS = 80;

// Filler words that would match nearly every article.
const STOPWORDS = new Set(
  "a an and are as at be by can do does for from how i in is it my of on or the to what when where why with you your".split(
    " ",
  ),
);

/** Light stemming so "share", "shared", "sharing" and "shares" all match each other (as substrings). */
function stem(word: string): string {
  for (const suffix of ["ing", "ed", "es", "s", "e"]) {
    if (word.endsWith(suffix) && word.length - suffix.length >= 3) return word.slice(0, -suffix.length);
  }
  return word;
}

function terms(query: string): string[] {
  const words = query
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  const meaningful = words.filter((w) => !STOPWORDS.has(w));
  // A query made only of filler words still searches for them.
  return [...new Set((meaningful.length ? meaningful : words).map(stem))];
}

function countIn(haystack: string, needles: string[]): number {
  const lower = haystack.toLowerCase();
  return needles.filter((term) => lower.includes(term)).length;
}

function snippetAround(text: string, needles: string[]): string {
  const lower = text.toLowerCase();
  const at = needles.map((term) => lower.indexOf(term)).find((i) => i >= 0) ?? 0;
  const start = Math.max(0, at - SNIPPET_RADIUS);
  const end = Math.min(text.length, at + SNIPPET_RADIUS);
  return `${start > 0 ? "…" : ""}${text.slice(start, end).trim()}${end < text.length ? "…" : ""}`;
}

/**
 * Client-side search over all article text: title, summary, keywords,
 * headings and body. Every query word must appear somewhere in the article;
 * titles and keywords outrank body matches.
 */
export function searchHelp(articles: HelpArticle[], query: string): HelpSearchResult[] {
  const needles = terms(query);
  if (needles.length === 0) return [];
  const results: HelpSearchResult[] = [];
  for (const article of articles) {
    const all = [article.title, article.summary, article.keywords.join(" "), article.intro]
      .concat(article.sections.flatMap((s) => [s.title, s.text]))
      .join(" ")
      .toLowerCase();
    if (!needles.every((term) => all.includes(term))) continue;

    let best: { id: string; title: string; text: string; score: number } | undefined;
    for (const section of article.sections) {
      const score = countIn(section.title, needles) * WEIGHT.heading + countIn(section.text, needles) * WEIGHT.text;
      if (score > 0 && score > (best?.score ?? 0)) best = { ...section, score };
    }
    const score =
      countIn(article.title, needles) * WEIGHT.title +
      countIn(article.keywords.join(" "), needles) * WEIGHT.keyword +
      countIn(article.summary, needles) * WEIGHT.summary +
      (best?.score ?? 0);
    // A title that matches the whole query is the answer: link the article, not a heading in it.
    const section = countIn(article.title, needles) === needles.length ? undefined : best;
    results.push({
      article,
      section: section && { id: section.id, title: section.title },
      snippet: section ? snippetAround(section.text || section.title, needles) : article.summary,
      score,
    });
  }
  return results.sort((a, b) => b.score - a.score);
}
